import React, { useEffect, useState, useRef } from "react";
import { submitAIChatBot } from "../../../../utils/chatApi";

const CommonTemplate = ({ selectedTemplate, selectedLayout, isTemplateId, widgetId, chatTabData }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const messagesEndRef = useRef(null);
    const [isBotWaiting, setIsBotWaiting] = useState(false);
    const [userInput, setUserInput] = useState("");
    const [currentTime, setCurrentTime] = useState('');
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [mobile_number, setMobileNumber] = useState("");
    const [isRegistered, setIsRegistered] = useState(false);
    const [errors, setErrors] = useState({
        name: false,
        email: false,
        mobile_number: false, 
    });
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    // Default values for the chatTabData
    const {
        is_website_frontend,
        is_from_dashboard = true,
        chat_window_name,
        chat_window_picture,
        welcome_message,
        chat_triggers_is_on_page_load,
        is_show_bubble_notification_badge,
        accent_color,
        theme_type
    } = chatTabData || {};

    // Call Css
    useEffect(() => {
        // Remove other module CSS
        const oldLinks = document.querySelectorAll('link[data-dynamic-css]');
        oldLinks.forEach(link => link.remove());

        // Load CSS
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = `${process.env.PUBLIC_URL}/ai-chatbot/temp-css/CommonTemplate.css`;
        if (widgetId) {
            link.setAttribute("ai-chatbot-data-dynamic-css", "ai-chatbot");
        } else {
            link.setAttribute("data-dynamic-css", "ai-chatbot");
        }
        document.head.appendChild(link);

        // Clean up on unmount
        return () => {
            const thisLink = document.querySelector('link[data-dynamic-css="ai-chatbot"]');
            if (thisLink) thisLink.remove();
        };
    }, [widgetId]);

    useEffect(() => {
        // Only open popup if page load is enabled and user is NOT from website frontend
        const shouldOpenPopup =
            Number(chat_triggers_is_on_page_load) === 1 &&
           is_website_frontend !== "website-frontend";

        setIsOpen(shouldOpenPopup);
    }, [is_website_frontend,chat_triggers_is_on_page_load]);

    useEffect(() => {
        // Function to update time
        const updateTime = () => {
            const now = new Date();
            const hours = now.getHours().toString().padStart(2, '0');
            const minutes = now.getMinutes().toString().padStart(2, '0');
            setCurrentTime(`${hours}:${minutes}`);
        };

        updateTime(); // initial call

        const interval = setInterval(updateTime, 60000);

        return () => clearInterval(interval);
    }, []);

    //Handle message
    const sendMessage = async () => {
        if (!userInput.trim()) return;

        const userMessage = {
            sender: "user",
            text: userInput,
        };

        setMessages((prev) => [...prev, userMessage]);
        setUserInput("");
        setIsBotWaiting(true);

        try {
            // Append text fields
            let name = sessionStorage.getItem("customerName");
            let email = sessionStorage.getItem("customerEmail");
            let mobile_number = sessionStorage.getItem("customerMobileNumber");
            let message = userInput;
            
            //Api Call
            let apiResponse = await submitAIChatBot(message,name,email,mobile_number,is_from_dashboard);

            setMessages((prev) => [
                ...prev,
                { sender: "bot", text: apiResponse.reply?.trim() || "Oops! Something went wrong." },
            ]);
        } catch (err) {
            setMessages((prev) => [
                ...prev,
                { sender: "bot", text: "Oops! Something went wrong." },
            ]);
        } finally {
            setIsBotWaiting(false);
        }
    };

    useEffect(() => {
        const savedEmail = sessionStorage.getItem("customerEmail");
        const savedName = sessionStorage.getItem("customerName");
        const savedMobileNumber = sessionStorage.getItem("customerMobileNumber");

        if (savedEmail) setEmail(savedEmail);
        if (savedName) setName(savedName);
        if (savedMobileNumber) setMobileNumber(savedMobileNumber);

        if (savedEmail && savedName && savedMobileNumber) {
            setIsRegistered(true);
        }
    }, []);

    // Scroll to bottom whenever messages change
    useEffect(() => {
        if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    // Check if chat_window_picture is a File (Blob) and convert it
    const imageUrl = chat_window_picture instanceof File
        ? URL.createObjectURL(chat_window_picture)
        : chat_window_picture || null;

    //Function for hanlde color
    function getReadableTextColor(bgColor) {
        if (!bgColor) return '#ffffff';
        const color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);

        // Formula for luminance
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
        return luminance > 186 ? '#000000' : '#ffffff';
    }
    const textColor = getReadableTextColor(accent_color);

    //Function for hanlde ai chat box background color
    function hexToRGBA(hex, alpha = 0.8) {
        if (!hex) return `rgba(255, 255, 255, ${alpha})`;

        // Remove hash
        hex = hex.replace('#', '');

        // Handle shorthand (e.g., #abc)
        if (hex.length === 3) {
            hex = hex.split('').map((char) => char + char).join('');
        }

        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    //Is Color Dark
    function isColorDark(hex) {
        const color = hex.charAt(0) === '#' ? hex.substring(1, 7) : hex;
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b);
        return luminance <= 186;
    }

    //Handle light color
    function getLightenedColor(hex, factor = 0.2) {
        hex = hex.replace('#', '');
        if (hex.length === 3) {
            hex = hex.split('').map((c) => c + c).join('');
        }

        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);

        // Lighten each channel
        r = Math.min(255, Math.floor(r + (255 - r) * factor));
        g = Math.min(255, Math.floor(g + (255 - g) * factor));
        b = Math.min(255, Math.floor(b + (255 - b) * factor));

        return `rgba(${r}, ${g}, ${b}, 0.8)`;
    }
    //Chat box background color
    const aiChatBoxColor =
    ['theme-4', 'theme-5'].includes(theme_type)
        ? (isColorDark(accent_color)
            ? getLightenedColor(accent_color, 0.8)  // Lighten by 80%
            : hexToRGBA(accent_color, 0.8))
        : undefined;
    const aiChatTextColor = getReadableTextColor(aiChatBoxColor || accent_color);

    //Handle Registration
    const handleRegistration = () => {
        const newErrors = {
            name: !name.trim(),
            email: !email.trim() || !emailPattern.test(email),
            mobile_number: !mobile_number.trim(),
        };
        setErrors(newErrors);

        if (!newErrors.name && !newErrors.email && !newErrors.mobile_number) {
            sessionStorage.setItem("customerEmail", email);
            sessionStorage.setItem("customerName", name);
            sessionStorage.setItem("customerMobileNumber", mobile_number);
            setIsRegistered(true);
        }
    };

    let head_accent_color = accent_color;
    let head_text_color = textColor;
    let themeClass = "";

    if (theme_type === 'theme-1' || theme_type === 'theme-3' || theme_type === 'theme-4') {
        head_accent_color = "#fff";
        head_text_color = "#000";
        themeClass = "theme-white-bg";
    } else if (theme_type === 'theme-6') {
        head_accent_color = "#000";
        head_text_color = "#fff";
        themeClass = "theme-black-bg";
    }

    let aiChatTemplateBoxColor = "";
    let aiChatInputBorderColor = "";
    if (theme_type === 'theme-6') {
        aiChatTemplateBoxColor = "1px solid #313131";
        aiChatInputBorderColor = "";
    } else {
        aiChatTemplateBoxColor = "1px solid #ddd";
        aiChatInputBorderColor = "1px solid rgba(17, 17, 17, 0.1)";
    }

    return (
        <>
            <div className={`upgadeify-ai-chat-template-main ${is_website_frontend}`} style={{
                "--accent-bg": accent_color,
                "--accent-text": textColor,
            }}>
                <div className={`upgadeify-ai-chat-template-inner right-side`}>
                    {isOpen && (
                        <div className="upgadeify-ai-chat-template">
                            <div className={`upgadeify-ai-chat-avatar-wrapper ${themeClass}`} style={{ backgroundColor: head_accent_color, color: head_text_color }}>
                                <div className="upgadeify-ai-chat-avatar">
                                    {imageUrl ? (
                                        <img src={imageUrl} alt="chat theme" />
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 100 100" width="36" height="36">
                                            <defs><linearGradient id="blot_gradient_-YpZwGFy" gradientTransform="rotate(82)"><stop stop-color={accent_color} stop-opacity="0.9" offset="34.498046260341226%"></stop><stop offset="160%" stop-color={accent_color} stop-opacity="0.9"></stop></linearGradient></defs>
                                            <path fill="url(#blot_gradient_-YpZwGFy)" d="M 96.51366004706134 38.161938379678276 Q 97.996461056 97.996461056, 51.01352910163916 97.98575864420876 Q 2.003538944000006 97.996461056, 3.5768699449300883 37.81175690305655 Q 2.0035389439999918 2.003538944000006, 60.12691336315523 3.0840549531902397 Q 97.99646105599999 2.0035389439999918, 96.51366004706134 38.161938379678276"></path>
                                            <defs><linearGradient id="blot_gradient_3hrABPkZ" gradientTransform="rotate(-18)"><stop stop-color={accent_color} stop-opacity="0.3" offset="1.4024430355482842%"></stop><stop offset="160%" stop-color={textColor} stop-opacity="0.3"></stop></linearGradient></defs>
                                            <path fill="url(#blot_gradient_3hrABPkZ)" d="M 99.87330133440221 46.4427800169352 Q 100 100, 48.71669685783502 99.98352861738864 Q 0 100, 0.2769942937164771 55.255730542264914 Q -1.4210854715202004e-14 0, 37.11866264716115 1.6877743422810312 Q 100 -1.4210854715202004e-14, 99.87330133440221 46.4427800169352"></path><defs><linearGradient id="stars_gradient_NeJqiXZ2" gradientTransform="rotate(90)"><stop offset="25%" stop-color={textColor}></stop><stop offset="75%" stop-color={textColor}></stop></linearGradient><clipPath id="stars_clip_path_TPyA26F7"><path d="M64.9144 22.938C64.7001 22.3589 63.8809 22.3589 63.6666 22.938L61.1294 29.7931C61.062 29.9751 60.9184 30.1187 60.7363 30.1861L53.8805 32.723C53.3013 32.9373 53.3013 33.7564 53.8805 33.9707L60.7363 36.5076C60.9184 36.575 61.062 36.7185 61.1294 36.9006L63.6666 43.7557C63.8809 44.3348 64.7001 44.3348 64.9144 43.7557L67.4516 36.9006C67.519 36.7185 67.6626 36.575 67.8446 36.5076L74.7005 33.9707C75.2797 33.7564 75.2797 32.9373 74.7005 32.723L67.8446 30.1861C67.6626 30.1187 67.519 29.9751 67.4516 29.7931L64.9144 22.938Z"></path><path d="M33.0547 29.0105C32.9016 28.5968 32.3165 28.5968 32.1634 29.0105L30.3511 33.907C30.303 34.037 30.2004 34.1395 30.0704 34.1877L25.1733 35.9998C24.7596 36.1528 24.7596 36.7379 25.1733 36.891L30.0704 38.7031C30.2004 38.7512 30.303 38.8537 30.3511 38.9838L32.1634 43.8803C32.3165 44.2939 32.9016 44.2939 33.0547 43.8803L34.867 38.9838C34.9151 38.8537 35.0177 38.7512 35.1477 38.7031L40.0448 36.891C40.4585 36.7379 40.4585 36.1528 40.0448 35.9998L35.1477 34.1877C35.0177 34.1395 34.9151 34.037 34.867 33.907L33.0547 29.0105Z"></path><path d="M47.4349 36.4773C47.833 35.4018 49.3543 35.4018 49.7524 36.4773L54.4643 49.2082C54.5895 49.5463 54.8561 49.8129 55.1942 49.938L67.9265 54.6494C69.0022 55.0474 69.0022 56.5686 67.9266 56.9666L55.1942 61.678C54.8561 61.8031 54.5895 62.0697 54.4643 62.4078L49.7524 75.1387C49.3543 76.2142 47.833 76.2142 47.4349 75.1387L42.723 62.4078C42.5978 62.0697 42.3312 61.8031 41.9931 61.678L29.2607 56.9666C28.1851 56.5686 28.1851 55.0474 29.2607 54.6494L41.9931 49.938C42.3312 49.8129 42.5978 49.5463 42.723 49.2082L47.4349 36.4773Z"></path></clipPath></defs><circle cx="50%" cy="50%" r="50%" clip-path="url(#stars_clip_path_TPyA26F7)" fill="url(#stars_gradient_NeJqiXZ2)"></circle>
                                        </svg>
                                    )}
                                </div>
                                <div className="upgadeify-ai-chat-avatar_content">
                                    <div className="upgadeify-ai-chat-avatar_name" style={{ color: head_text_color}}>{chat_window_name}</div>
                                </div>
                                <div className="close-chat-poup">
                                    <button onClick={() => setIsOpen(false)} style={{ color: head_text_color}}>X</button>
                                </div>
                            </div>
                            <div className={`upgadeify-ai-chat-template-box ${theme_type} ${themeClass}`} style={{ borderTop: aiChatTemplateBoxColor}}>
                                {!isRegistered && !is_from_dashboard? (
                                    <div className="chat-window" style={{
                                            backgroundColor: aiChatBoxColor,
                                            color: aiChatTextColor,
                                        }}>
                                        <p style={{ color: head_text_color}}>Please fill this form before chat</p>
                                        <input 
                                            type="text" 
                                            placeholder="Full Name" 
                                            value={name} 
                                            onChange={(e) => setName(e.target.value)} onKeyDown={(e)=> {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleRegistration();
                                                }
                                            }}
                                            className={errors.name ? "input-error" : ""}
                                        />
                                        <input 
                                            type="email" 
                                            placeholder="Email Address" 
                                            value={email} 
                                            onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleRegistration();
                                                }
                                            }}
                                            className={errors.email ? "input-error" : ""}
                                        />
                                        <input
                                            type="text"
                                            placeholder="Mobile Number"
                                            value={mobile_number}
                                            maxLength={10}
                                            onChange={(e) => setMobileNumber(e.target.value.replace(/\D/g, ''))}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleRegistration();
                                                }
                                            }}
                                            className={errors.mobile_number ? "input-error" : ""}
                                        />
                                        <button onClick={handleRegistration} style={{backgroundColor: accent_color,color: textColor}}>Start Chat</button>
                                    </div>
                                ) : (
                                    <>
                                    <div className="upgadeify-ai-chat_box" style={{
                                            backgroundColor: aiChatBoxColor,
                                            color: aiChatTextColor,
                                        }}
                                        >
                                        <div className="upgadeify-ai-chat-box_content">
                                            <div className="upgadeify-ai-chat-box_text" style={{ backgroundColor: "#fff", color: "#000", whiteSpace: 'pre-line'}}>
                                            {welcome_message}
                                            <div className="upgadeify-ai-chat-time" style={{ color: textColor, opacity: 0.5 }}>{currentTime}</div>
                                            </div>
                                            {messages.map((msg, i) => (
                                                <div key={i} className={`upgadeify-ai-chat-message ${msg.sender === "user" ? "user" : "bot"}`}style={
                                                    msg.sender === "user"
                                                        ? { backgroundColor: accent_color, color: textColor, whiteSpace: 'pre-line' }
                                                        : { backgroundColor: "#fff", color: "#000", whiteSpace: 'pre-line' }
                                                    }>{msg.text}</div>
                                            ))}
                                        </div>
                                        {isBotWaiting && (
                                            <div className="upgadeify-ai-chat-message bot">
                                                <div className="upgadeify-ai-chat-typing-indicator">
                                                    <span>.</span><span>.</span><span>.</span>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                    <div className="upgadeify-ai-chat-input" style={{ borderTop: aiChatInputBorderColor}}>
                                        <input
                                        type="text"
                                        value={userInput}
                                        onChange={(e) => setUserInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                            e.preventDefault();
                                            sendMessage();
                                            }
                                        }}
                                        placeholder="Type your message..."
                                        />
                                        <button onClick={sendMessage} style={{backgroundColor: accent_color,color: textColor}}>
                                            <svg width="14" height="14" viewBox="0 0 14 14" fill="#fff" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M1.0227 0.0701843C0.899449 0.0110857 0.761821 -0.0113913 0.626169 0.00542314C0.490518 0.0222376 0.362544 0.0776366 0.257449 0.16504C0.152355 0.252443 0.0745583 0.368176 0.0333006 0.49849C-0.00795707 0.628805 -0.0109412 0.768224 0.0247023 0.900184L1.1767 5.20418C1.20551 5.31222 1.26541 5.40943 1.34896 5.48373C1.43251 5.55804 1.53604 5.60618 1.6467 5.62218L7.2957 6.42918C7.4587 6.45218 7.4587 6.68918 7.2957 6.71218L1.6477 7.51818C1.53709 7.53431 1.43362 7.5825 1.35009 7.65678C1.26656 7.73107 1.20663 7.82821 1.1777 7.93618L0.0247023 12.2432C-0.0109412 12.3751 -0.00795707 12.5146 0.0333006 12.6449C0.0745583 12.7752 0.152355 12.8909 0.257449 12.9783C0.362544 13.0657 0.490518 13.1211 0.626169 13.1379C0.761821 13.1548 0.899449 13.1323 1.0227 13.0732L13.3067 7.21618C13.4284 7.15797 13.5311 7.06651 13.6031 6.95238C13.675 6.83824 13.7132 6.70609 13.7132 6.57118C13.7132 6.43628 13.675 6.30413 13.6031 6.18999C13.5311 6.07586 13.4284 5.9844 13.3067 5.92618L1.0227 0.0701843Z" fill="black"/>
                                            </svg>
                                        </button>
                                    </div>
                                </>
                                )}
                            </div>
                        </div>
                    )}

                    <div className='upgadeify-ai-chat-button'>
                        <div className="upgadeify-ai-chat-button-inner">
                             <div class="waves-block">
                                <div class="waves wave-1"  style={{backgroundColor: accent_color}}></div>
                                <div class="waves wave-2"  style={{backgroundColor: accent_color}}></div>
                                <div class="waves wave-3"  style={{backgroundColor: accent_color}}></div>
                        </div>
                        <button className={`upgadeify-ai-button`} onClick={() => setIsOpen(!isOpen)} style={{backgroundColor: accent_color, color: textColor}}>
                            {/* SVG Icon for ai Chat Bubble */}
                            {(selectedTemplate === 'Virtual_Assistant_Temp' || selectedTemplate === 'Virtual_Assistant_Temp2' || selectedTemplate === 'Virtual_Assistant_Temp3' || selectedTemplate === 'Virtual_Assistant_Temp4' || selectedTemplate === 'Virtual_Assistant_Temp5' || selectedTemplate === 'Dark_Theme_Temp' ) && (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25" className="injected-svg"><path d="M11.7209 4C8.73493 4 5.94981 5.50411 4.31229 8.00105C2.96622 10.0536 2.96622 12.7091 4.31229 14.7617C5.02705 15.8515 5.96015 16.7521 7.03345 17.4211L7.04953 17.4312C7.2083 17.5301 7.3534 17.6206 7.47025 17.7006C7.59014 17.7827 7.73473 17.8896 7.86408 18.0324C8.03125 18.2169 8.14481 18.3955 8.24095 18.6252C8.35538 18.8985 8.39166 19.2089 8.42238 19.4717C8.4253 19.4966 8.42816 19.5211 8.43103 19.5451L8.6117 21.0529L10.6524 19.3516C10.661 19.3444 10.6697 19.3372 10.6785 19.3298C10.7778 19.2467 10.8972 19.1469 11.0323 19.0648C11.2681 18.9215 11.5301 18.8266 11.803 18.7857C11.9593 18.7622 12.1149 18.7625 12.2443 18.7627C12.2559 18.7627 12.2672 18.7627 12.2783 18.7627C15.2643 18.7627 18.0495 17.2586 19.687 14.7617C21.033 12.7091 21.033 10.0536 19.687 8.00105C18.0495 5.50411 15.2643 4 12.2783 4H11.7209ZM2.63985 6.90425C4.64703 3.84365 8.06087 2 11.7209 2H12.2783C15.9384 2 19.3522 3.84365 21.3594 6.90425C23.1422 9.62276 23.1422 13.1399 21.3594 15.8585C19.3522 18.9191 15.9384 20.7627 12.2783 20.7627C12.1903 20.7627 12.1452 20.7628 12.1118 20.7638C12.1038 20.764 12.0983 20.7643 12.0949 20.7644C12.088 20.7659 12.0814 20.7683 12.0752 20.7716C12.0724 20.7736 12.068 20.777 12.0617 20.7819C12.0355 20.8025 12.0007 20.8314 11.9331 20.8877L8.4795 23.767L8.47713 23.769L8.46629 23.7778C8.45843 23.7841 8.43539 23.8026 8.40429 23.8239L8.40316 23.8247C8.35969 23.8545 8.16191 23.99 7.86813 23.9987C7.59227 24.0068 7.32529 23.9007 7.13029 23.7054C6.92261 23.4975 6.87185 23.2631 6.86069 23.2116L6.8604 23.2103C6.85241 23.1734 6.84832 23.1442 6.84694 23.1342L6.84513 23.1204L6.84475 23.1173L6.44524 19.783C6.42354 19.602 6.41127 19.5019 6.39883 19.4288C6.39541 19.4087 6.39267 19.3952 6.39086 19.3869C6.38134 19.3797 6.36523 19.3679 6.3404 19.3509C6.26338 19.2981 6.1555 19.2306 5.97541 19.1184C4.65989 18.2983 3.51584 17.1942 2.63985 15.8585C0.857027 13.1399 0.857028 9.62276 2.63985 6.90425Z"></path></svg>
                            )}
                            {Number(is_show_bubble_notification_badge) === 1 && (
                                <div className="upgadeify-ai-chat-alert">1</div>
                            )}
                        </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CommonTemplate;
